import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/http";

/**
 * MilestoneOverlay — reusable celebratory pop-up used across the app
 * (water 2 L reached, training completed, streak milestones, level-ups…).
 *
 * Design guarantees:
 *   - The mascot PNG sits directly on top of the app UI. There is no
 *     white card, no rounded box — just a soft dimmed radial glow behind
 *     the image so it pops against any content underneath.
 *   - Spring scale-in + gentle idle sway. Auto-dismisses after ~5 s.
 *   - Optional confetti / droplet burst with a caller-controlled colour
 *     palette (default: cyan for water, orange for fitness).
 *
 * Props:
 *   show       boolean   controls visibility
 *   onClose    fn        called on tap-outside, close-button, or timeout
 *   mascotSrc  string    path to the transparent PNG asset
 *   mascotAlt  string    alt text
 *   title      string    big headline (e.g. "2 L geschafft! 💧")
 *   subtitle   string    supporting line (e.g. "Ottimo lavoro!")
 *   confettiColors  string[]  two hex colours for the particle burst
 *   autoDismissMs   number    default 5200; pass 0 to disable
 *   size            "md" | "lg"  mascot render size (md=256px, lg=288px)
 */
export default function MilestoneOverlay({
  show,
  onClose,
  mascotSrc,
  mascotAlt = "Solano",
  title,
  subtitle,
  confettiColors = ["#5FB3D8", "#7ECEE5"],
  autoDismissMs = 5200,
  size = "md",
  testId = "milestone-overlay",
}) {
  useEffect(() => {
    if (!show || !autoDismissMs) return undefined;
    const id = setTimeout(() => onClose?.(), autoDismissMs);
    return () => clearTimeout(id);
  }, [show, autoDismissMs, onClose]);

  // Mascot gallery: record the moment as unlocked (backend whitelists keys).
  useEffect(() => {
    if (show) api.post("/client/mascots/unlock", { key: testId }).catch(() => {});
  }, [show, testId]);

  const [c1, c2] = confettiColors;
  const imgW = size === "lg" ? "w-72 sm:w-80" : "w-64 sm:w-72";

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-auto"
          data-testid={testId}
          onClick={onClose}
          role="button"
          aria-label="Close celebration"
        >
          {/* Dimmed radial glow — no white card, mascot floats over app UI. */}
          <div
            className="absolute inset-0"
            style={{ background: "radial-gradient(closest-side at 50% 45%, rgba(21,60,64,0.55), rgba(21,60,64,0.15) 55%, rgba(21,60,64,0) 78%)" }}
          />

          {/* Confetti burst — 10 particles, alternating palette colours. */}
          {[...Array(10)].map((_, i) => {
            const left = 5 + (i * 9) + (i % 2 === 0 ? 3 : -3);
            const sz = 8 + (i % 3) * 4;
            const delay = 0.15 + (i * 0.08);
            return (
              <motion.div
                key={i}
                className="absolute rounded-full pointer-events-none"
                style={{
                  left: `${left}%`,
                  top: "42%",
                  width: sz,
                  height: sz,
                  background: i % 2 === 0 ? c1 : c2,
                  boxShadow: `0 0 12px ${c1}99`,
                }}
                initial={{ opacity: 0, y: 0, scale: 0 }}
                animate={{
                  opacity: [0, 1, 1, 0],
                  y: [0, -60 - (i % 4) * 20, 40, 120],
                  x: [(i % 2 === 0 ? -10 : 10), (i % 2 === 0 ? -30 : 30), (i % 2 === 0 ? -50 : 50)],
                  scale: [0, 1.1, 1, 0.8],
                }}
                transition={{ duration: 2.4, delay, ease: "easeOut" }}
              />
            );
          })}

          <motion.div
            className="relative flex flex-col items-center px-4"
            initial={{ opacity: 0, scale: 0.7, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.7, y: 20 }}
            transition={{ type: "spring", stiffness: 180, damping: 14, delay: 0.05 }}
            onClick={(e) => e.stopPropagation()}
          >
            <motion.img
              src={mascotSrc}
              alt={mascotAlt}
              draggable={false}
              className={`${imgW} select-none pointer-events-none`}
              style={{ filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.35))" }}
              animate={{ rotate: [-2, 2, -2], y: [0, -6, 0] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
              data-testid={`${testId}-mascot`}
            />
            {title && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.4 }}
                className="mt-4 text-center font-heading text-2xl sm:text-3xl font-bold text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.65)]"
                data-testid={`${testId}-title`}
              >
                {title}
              </motion.p>
            )}
            {subtitle && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.4 }}
                className="mt-1 text-center text-white/90 text-sm sm:text-base drop-shadow-[0_1px_6px_rgba(0,0,0,0.6)]"
              >
                {subtitle}
              </motion.p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
